import { useFormContext, useWatch } from 'react-hook-form';
import { P, Section, Stack } from '@fashionnovateam/nova-ui-toolkit';
import { useEffect, useMemo } from 'react';
import { ControlledFormInput } from '../../../../Lib/Utils/FormUtils/ControlledFormInput';
import { ItemDetailsFormData } from './types';
import { useItemDetailsForm } from './useItemDetailForm';

export function FabricDetailsSection() {
  const { control, setValue } = useFormContext<ItemDetailsFormData>();
  const { isFieldDisabled, isFieldRequired, makeOptionsFromMetaData } =
    useItemDetailsForm();

  const [currentFabric, currentPatternPrint, currentWash] = useWatch({
    control,
    name: ['fabric', 'patternPrint', 'wash'],
  });

  const fabricOptions = useMemo(
    () => makeOptionsFromMetaData('ns_master_fabrics', 'Fabric'),
    [makeOptionsFromMetaData],
  );
  const patternPrintOptions = useMemo(
    () => makeOptionsFromMetaData('ns_master_patterns', 'Pattern/Print'),
    [makeOptionsFromMetaData],
  );
  const washOptions = useMemo(
    () => makeOptionsFromMetaData('ns_master_wash', 'Wash'),
    [makeOptionsFromMetaData],
  );

  useEffect(() => {
    if (
      currentFabric &&
      fabricOptions.length > 0 &&
      !fabricOptions.some((opt) => opt.value === currentFabric)
    ) {
      setValue('fabric', '');
    }
  }, [fabricOptions]);

  useEffect(() => {
    if (
      currentPatternPrint &&
      patternPrintOptions.length > 0 &&
      !patternPrintOptions.some((opt) => opt.value === currentPatternPrint)
    ) {
      setValue('patternPrint', '');
    }
  }, [patternPrintOptions]);

  useEffect(() => {
    if (
      currentWash &&
      washOptions.length > 0 &&
      !washOptions.some((opt) => opt.value === currentWash)
    ) {
      setValue('wash', '');
    }
  }, [washOptions]);

  return (
    <Section
      icon="scale"
      title="Fabric Details"
      color="pink"
      className="w-full flex flex-col"
    >
      <Stack direction="col" gap="md">
        <Stack direction="row" gap="md">
          <ControlledFormInput
            name="fabricWeight"
            label="Fabric Weight (GSM)"
            control={control}
            inputType="number"
            inputProps={{
              placeholder: '0',
              className: 'w-full',
              min: '0',
              endAdornment: (
                <P size="xs" color="disabled">
                  gsm
                </P>
              ),
            }}
          />
        </Stack>
        <Stack direction="row" gap="md">
          <ControlledFormInput
            name="fabricCode"
            label="Fabric Code & Name"
            control={control}
            inputType="text"
            inputProps={{ className: 'w-full' }}
          />
          <ControlledFormInput
            name="fabric"
            label="Fabric Type"
            control={control}
            inputType="multiselect"
            disabled={
              isFieldDisabled('fabric', false) || fabricOptions.length <= 0
            }
            required={isFieldRequired('fabric', true)}
            inputProps={{
              searchable: true,
              singleSelectWhenSearchable: true,
              className: 'w-full',
              placeholder: 'Select..',
              options: fabricOptions,
            }}
          />
        </Stack>
        <Stack direction="row" gap="md">
          <ControlledFormInput
            name="patternPrint"
            label="Pattern/Print"
            control={control}
            inputType="multiselect"
            disabled={
              isFieldDisabled('pattern_print', false) ||
              patternPrintOptions.length <= 0
            }
            required={isFieldRequired('pattern_print', true)}
            inputProps={{
              searchable: true,
              singleSelectWhenSearchable: true,
              className: 'w-full',
              placeholder: 'Select..',
              options: patternPrintOptions,
            }}
          />
          <ControlledFormInput
            name="wash"
            label="Wash"
            control={control}
            inputType="multiselect"
            disabled={isFieldDisabled('wash', false) || washOptions.length <= 0}
            required={isFieldRequired('wash', false)}
            inputProps={{
              searchable: true,
              singleSelectWhenSearchable: true,
              className: 'w-full',
              placeholder: 'Select..',
              options: washOptions,
            }}
          />
        </Stack>
      </Stack>
    </Section>
  );
}
